import { OperatorElearningTopicCategoryTranslationService } from '../../../../../api/api-services/operator-elearning-topic-category-translation.service';
import { IOperatorElearningTopicCategoryTranslationIm } from '../../../../../api/operator-elearning-topic-category-translation-im.model';
import { IOperatorElearningTopicCategoryTranslationUm } from '../../../../../api/operator-elearning-topic-category-translation-um.model';
import { OperatorElearningTopicCategoryService } from '../../../../../api/api-services/operator-elearning-topic-category.service';
import { IOperatorElearningTopicCategoryVm } from '../../../../../api/operator-elearning-topic-category-vm.model';
import { IOperatorElearningTopicCategoryUm } from '../../../../../api/operator-elearning-topic-category-um.model';
import { OperatorAvailableExamsService } from '../../../../../api/api-services/operator-available-exams.service';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  OnChanges,
} from '@angular/core';
import { IDetailsControl } from '../../../../shared/models/details-control';
import { IExamViewModel } from '../../../../../api/exam-view-model.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-category-details',
  templateUrl: './category-details.component.html',
  styleUrl: './category-details.component.scss',
})
export class CategoryDetailsComponent implements OnInit, OnChanges {
  @Input() category!: IOperatorElearningTopicCategoryVm;
  @Input() categoryList!: IOperatorElearningTopicCategoryVm[];
  @Input() categoryIndex!: number;
  @Input() detailsControl: IDetailsControl = {
    isEditing: false,
    isOpen: false,
  };

  @Output() detailsControlChange = new EventEmitter<IDetailsControl>();
  @Output() refreshCategories = new EventEmitter<void>();

  private _operatorElearningTopicCategoryTranslationService = inject(
    OperatorElearningTopicCategoryTranslationService
  );
  private _operatorElearningTopicCategoryService = inject(
    OperatorElearningTopicCategoryService
  );
  private _operatorAvailableExamsService = inject(
    OperatorAvailableExamsService
  );
  private _messageService = inject(MessageService);
  private _fb = inject(FormBuilder);

  public isLoading = false;
  public isSaving = false;

  public availableLanguages = [
    { code: 'pl', name: 'Polski', isDefault: true },
    { code: 'en', name: 'Angielski', isDefault: false },
    { code: 'uk', name: 'Ukraiński', isDefault: false },
    { code: 'de', name: 'Niemiecki', isDefault: false },
  ];

  public translationForms: { [key: string]: FormGroup } = {};
  public editingTranslation: { [key: string]: boolean } = {};
  public savingTranslation: { [key: string]: boolean } = {};
  public availableExams: IExamViewModel[] = [];
  public categoryForm!: FormGroup;

  ngOnInit(): void {
    this.loadAvailableExams();
    this.initializeForms();
  }

  ngOnChanges(): void {
    if (this.category && this.detailsControl.isOpen) {
      this.initializeForms();
    }
  }

  private loadAvailableExams(): void {
    this._operatorAvailableExamsService.get().subscribe({
      next: (exams) => {
        this.availableExams = exams;
      },
      error: (error) => {
        this.availableExams = [];
      },
    });
  }

  private initializeForms(): void {
    if (!this.category) return;

    const assignedExamGuids =
      this.category.examCategories?.map((e) => e.guid) || [];
    this.categoryForm = this._fb.group({
      name: [this.category.name || '', Validators.required],
      isActive: [this.category.isActive],
      order: [this.category.order || 0, [Validators.min(0)]],
      examsGuid: [assignedExamGuids],
    });

    this.translationForms = {};
    this.editingTranslation = {};
    this.savingTranslation = {};

    this.availableLanguages.forEach((lang) => {
      const existingTranslation = this.category.topicCategoryTranslations?.find(
        (t: any) => t.languageCode === lang.code
      );

      this.translationForms[lang.code] = this._fb.group({
        id: [existingTranslation?.id || 0],
        languageCode: [lang.code, Validators.required],
        elearningTopicCategoryId: [this.category.id, Validators.required],
        name: [existingTranslation?.nameOverride || '', Validators.required],
      });

      this.editingTranslation[lang.code] = false;
      this.savingTranslation[lang.code] = false;
    });
  }

  public closeDetails(): void {
    this.detailsControl.isOpen = false;
    this.detailsControl.isEditing = false;
    this.detailsControlChange.emit(this.detailsControl);
    this.resetEditingStates();
  }

  public hideDialog(): void {
    this.detailsControl.isOpen = false;
    this.detailsControl.isEditing = false;
    this.resetEditingStates();
    this.detailsControlChange.emit(this.detailsControl);
  }

  public enableTranslationEditing(languageCode: string): void {
    if (!this.canEditTranslation(languageCode)) {
      return;
    }
    this.editingTranslation[languageCode] = true;
  }

  public cancelTranslationEditing(languageCode: string): void {
    this.editingTranslation[languageCode] = false;
    const existingTranslation = this.category.topicCategoryTranslations?.find(
      (t: any) => t.languageCode === languageCode
    );

    if (existingTranslation) {
      this.translationForms[languageCode].patchValue({
        name: existingTranslation.nameOverride,
      });
    } else {
      this.translationForms[languageCode].patchValue({
        name: '',
      });
    }
  }

  public saveTranslation(languageCode: string): void {
    const form = this.translationForms[languageCode];
    if (form.invalid) {
      this.markFormGroupTouched(form);
      return;
    }

    this.savingTranslation[languageCode] = true;
    const formValue = form.value;
    const existingTranslation = this.category.topicCategoryTranslations?.find(
      (t: any) => t.languageCode === languageCode
    );

    if (
      existingTranslation &&
      existingTranslation.id &&
      existingTranslation.id > 0
    ) {
      const updateData: IOperatorElearningTopicCategoryTranslationUm = {
        id: existingTranslation.id,
        name: formValue.name,
      };

      this._operatorElearningTopicCategoryTranslationService
        .put(updateData)
        .subscribe({
          next: (response: any) => {
            this.handleTranslationSaveSuccess(
              languageCode,
              { ...existingTranslation, nameOverride: formValue.name },
              'zaktualizowane'
            );
          },
          error: (error: any) => {
            this.handleTranslationSaveError(
              languageCode,
              error,
              'zaktualizować'
            );
          },
        });
    } else {
      const newTranslation: IOperatorElearningTopicCategoryTranslationIm = {
        elearningTopicCategoryId: formValue.elearningTopicCategoryId,
        name: formValue.name,
        languageCode: formValue.languageCode,
      };

      this._operatorElearningTopicCategoryTranslationService
        .post(newTranslation)
        .subscribe({
          next: (response: any) => {
            form.patchValue({ id: response.id });
            this.handleTranslationSaveSuccess(
              languageCode,
              response,
              'utworzone'
            );
          },
          error: (error: any) => {
            this.handleTranslationSaveError(languageCode, error, 'utworzyć');
          },
        });
    }
  }

  public deleteTranslation(languageCode: string): void {
    const existingTranslation = this.category.topicCategoryTranslations?.find(
      (t: any) => t.languageCode === languageCode
    );

    if (!existingTranslation || existingTranslation.id <= 0) {
      this.translationForms[languageCode].patchValue({
        name: '',
      });
      return;
    }

    this.savingTranslation[languageCode] = true;

    this._operatorElearningTopicCategoryTranslationService
      .delete(existingTranslation.id)
      .subscribe({
        next: (response: any) => {
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: `Tłumaczenie zostało usunięte`,
          });

          if (this.category.topicCategoryTranslations) {
            this.category.topicCategoryTranslations =
              this.category.topicCategoryTranslations.filter(
                (t: any) => t.languageCode !== languageCode
              );
          }

          this.translationForms[languageCode].patchValue({
            id: 0,
            name: '',
          });

          this.savingTranslation[languageCode] = false;
          this.editingTranslation[languageCode] = false;
          this.refreshCategories.emit();
        },
        error: (error: any) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się usunąć tłumaczenia',
          });
          this.savingTranslation[languageCode] = false;
        },
      });
  }

  public hasTranslation(languageCode: string): boolean {
    return (
      this.category.topicCategoryTranslations?.some(
        (t: any) => t.languageCode === languageCode && t.nameOverride
      ) || false
    );
  }

  public getTranslationName(languageCode: string): string {
    const translation = this.category.topicCategoryTranslations?.find(
      (t: any) => t.languageCode === languageCode
    );
    return translation?.nameOverride || '';
  }

  public getExamNamesDisplay(): string {
    if (
      this.category.examCategories &&
      this.category.examCategories.length > 0
    ) {
      return this.category.examCategories
        .map((exam: any) => exam.examName)
        .join(', ');
    }
    return '';
  }

  private handleTranslationSaveSuccess(
    languageCode: string,
    translationData: any,
    action: string
  ): void {
    this._messageService.add({
      severity: 'success',
      summary: 'Sukces',
      detail: `Tłumaczenie zostało ${action}`,
    });

    if (!this.category.topicCategoryTranslations) {
      this.category.topicCategoryTranslations = [];
    }

    const existingIndex = this.category.topicCategoryTranslations.findIndex(
      (t: any) => t.languageCode === languageCode
    );

    if (existingIndex >= 0) {
      this.category.topicCategoryTranslations[existingIndex] = translationData;
    } else {
      this.category.topicCategoryTranslations.push(translationData);
    }

    this.editingTranslation[languageCode] = false;
    this.savingTranslation[languageCode] = false;
    this.refreshCategories.emit();
  }

  private handleTranslationSaveError(
    languageCode: string,
    error: any,
    action: string
  ): void {
    this._messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: `Nie udało się ${action} tłumaczenia`,
    });
    this.savingTranslation[languageCode] = false;
  }

  private resetEditingStates(): void {
    Object.keys(this.editingTranslation).forEach((lang) => {
      this.editingTranslation[lang] = false;
      this.savingTranslation[lang] = false;
    });
    this.isSaving = false;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control) {
        control.markAsTouched({ onlySelf: true });
      }
    });
  }

  public editToggle(): void {
    if (!this.detailsControl.isEditing && this.isAnyTranslationBeingEdited()) {
      return;
    }

    this.detailsControl.isEditing = !this.detailsControl.isEditing;
    this.detailsControlChange.emit(this.detailsControl);

    if (!this.detailsControl.isEditing) {
      this.initializeForms();
    }
  }

  public saveCategory(): void {
    if (this.categoryForm.invalid) {
      this.markFormGroupTouched(this.categoryForm);
      return;
    }

    this.isSaving = true;
    const formValue = this.categoryForm.value;

    const updateData: IOperatorElearningTopicCategoryUm = {
      id: this.category.id,
      name: formValue.name,
      isActive: formValue.isActive,
      order: formValue.order,
      examsGuid: formValue.examsGuid || [],
    };

    this._operatorElearningTopicCategoryService.put(updateData).subscribe({
      next: (response: any) => {
        this.category.name = formValue.name;
        this.category.isActive = formValue.isActive;
        this.category.order = formValue.order;
        this.category.examCategories = this.getSelectedExams(
          formValue.examsGuid || []
        );

        this._messageService.add({
          severity: 'success',
          summary: 'Sukces',
          detail: 'Dział został zaktualizowany',
        });

        this.detailsControl.isEditing = false;
        this.detailsControlChange.emit(this.detailsControl);
        this.refreshCategories.emit();
        this.isSaving = false;
      },
      error: (error: any) => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się zaktualizować działu',
        });
        this.isSaving = false;
      },
    });
  }

  public deleteCategory(): void {
    this._operatorElearningTopicCategoryService
      .delete(this.category.id)
      .subscribe({
        next: (res) => {
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: 'Dział został usunięty',
          });
          this.hideDialog();
          this.refreshCategories.emit();
        },
        error: (error) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail:
              'Nie udało się usunąć działu, upewnij się, że nie ma powiązanych rozdziałów',
          });
        },
      });
  }

  private getSelectedExams(examGuids: string[]): IExamViewModel[] {
    return this.availableExams.filter((exam) => examGuids.includes(exam.guid));
  }

  public isAnyTranslationBeingEdited(): boolean {
    return Object.values(this.editingTranslation).some(
      (isEditing) => isEditing
    );
  }

  public canEditTranslation(languageCode: string): boolean {
    return (
      !this.detailsControl.isEditing &&
      !this.isAnyOtherTranslationBeingEdited(languageCode)
    );
  }

  private isAnyOtherTranslationBeingEdited(
    excludeLanguageCode: string
  ): boolean {
    return Object.keys(this.editingTranslation)
      .filter((lang) => lang !== excludeLanguageCode)
      .some((lang) => this.editingTranslation[lang]);
  }
}
